import { Button, Spinner, Surface, Text } from '@quality24/design-system';
import React from 'react';
import * as Sentry from '@sentry/react';

import { gql, useQuery } from '@apollo/client';
import { FrontEndError } from '@quality24/error-codes';
import {
  Hospital,
  JsonHospitalConfig,
  Organization,
} from '@quality24/inpatient-typings';
import { Navigate } from 'react-router';
import { useAuth } from '@/inpatient-dashboard/hooks/auth/useAuth';
import styles from './ProvisionPage.module.scss';

const GET_HOSPITAL = gql`
  query FetchUserData($hospitalId: UUID!) {
    hospital: hospitalById(id: $hospitalId) {
      id
      name
      config
      lgpdConsentHtml
      modules
      organization {
        id
        name
        config
        modules
      }
    }
  }
`;

interface HospitalQueryResult {
  hospital:
    | (Pick<
        Hospital,
        'id' | 'name' | 'config' | 'lgpdConsentHtml' | 'modules'
      > & {
        organization: Pick<Organization, 'id' | 'name' | 'config' | 'modules'>;
      })
    | null;
}

const ProvisionPage: React.FunctionComponent = () => {
  const { loginUrl, userData, isAuthenticated, addData, signOut } = useAuth();

  // Get Organization Config
  const { loading } = useQuery<HospitalQueryResult>(GET_HOSPITAL, {
    variables: {
      hospitalId: userData?.['custom:hospital_id'],
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
    skip:
      !!userData.isUserProvisioned ||
      !userData?.sub ||
      !userData?.['custom:hospital_id'] ||
      userData?.['custom:role']?.toUpperCase() === 'DEVICE',
    onCompleted: (data) => {
      addData({
        isUserProvisioned: true,
        hospitalName: data?.hospital?.name,
        hospitalConfig: {
          ...(data?.hospital?.organization.config || {}),
          ...(data?.hospital?.config || {}),
        } as JsonHospitalConfig,
        hospitalLgpdConsentHtml: data?.hospital?.lgpdConsentHtml,
        organizationName: data?.hospital?.organization.name,
        modules: data.hospital?.modules || data.hospital?.organization.modules,
      });
    },
  });

  const userId = userData['custom:user_id'] ?? userData.sub;

  /** Set user data for Sentry */
  React.useEffect(() => {
    Sentry.setUser({
      id: userId,
      enableMonitoring: userData.hospitalConfig?.enableMonitoring || false,
    });
  }, [userId, userData.hospitalConfig?.enableMonitoring]);

  if (userData?.['custom:role']?.toUpperCase() === 'DEVICE') {
    // User not allowed
    signOut(FrontEndError.Q1011);
  }

  if (loading || !userData.isUserProvisioned) {
    return (
      <div className={styles.wrapper}>
        <Surface>
          <div className="p-4">
            <Spinner className="mb-5" size="sm" />
          </div>
        </Surface>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/main" replace />;
  }

  if (!!Object.keys(userData).length && !isAuthenticated) {
    return (
      <div className={styles.wrapper}>
        <Surface>
          <div className="p-4">
            <Spinner className="mb-5" size="sm" />
          </div>
          <Text size="lg1" className="mb-5">
            Sua sessão expirou. Aguarde enquanto tentamos renová-la.
          </Text>

          <Text className="mt-4 mb-2" size="sm1">
            Se preferir, clique na opção abaixo para fazer login novamente:
          </Text>
          <Button
            size="sm"
            variant="link"
            onClick={() => window.location.assign(loginUrl)}
          >
            Ir para login
          </Button>
        </Surface>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Surface>
        <div className="p-4">
          <Spinner className="mb-5" size="sm" />
        </div>
        <Text size="lg1" className="mb-5">
          Aguarde enquanto te redirecionamos para o login...
        </Text>

        <Text className="mb-2" size="sm1">
          Se nada acontecer, clique no botão abaixo
        </Text>
        <Button
          size="md"
          variant="link"
          onClick={() => window.location.assign(loginUrl)}
        >
          Ir para login
        </Button>
      </Surface>
    </div>
  );
};

export default ProvisionPage;
